import React, {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
} from "react";

import { useResizeObserver } from "../../hooks/useResizeObserver";
import { scrollIntoView } from "../../utils/dom";

import "./horizontal-scrollable.css";

/** Describes the result of a useRef on HorizontalScrollable */
export interface IHorizontalScrollableRef {
	scrollIntoView(
		index: number,
		options?: {
			animate?: boolean;
		},
	): void;
}

/**
 * A component with horizontal scrollable content
 * When scrolled, the sides will have a fade effect (set fadeDistance to 0 to disable)
 * To be able to fade to the background, a CSS variable with the background color can be set (--scrollable-background-rgb: default is white)
 */
export const HorizontalScrollable = forwardRef<
	IHorizontalScrollableRef,
	React.PropsWithChildren<{
		/** Fade right side when scrollable */
		fadeRight?: boolean;
		/** The scroll distance that will influence opacity */
		fadeDistance?: number;
		/** Extra classes used on component */
		className?: string;
		/** Class place on the container around this child elements */
		containerClassName?: string;
		/** The default distance to stay from the side when scrolling something into view */
		defaultScrollIntoViewMargin?: number;
	}>
>(
	(
		{
			children,
			className,
			containerClassName,
			fadeRight = false,
			fadeDistance = 40,
			defaultScrollIntoViewMargin = 40,
		},
		parentRef,
	) => {
		const containerRef = useRef<HTMLDivElement>(null);
		const fadeRightRef = useRef<HTMLDivElement>(null);

		const update = useCallback(() => {
			const containerEl = containerRef.current;
			if (!containerEl) return;

			const fadeRightEl = fadeRightRef.current;
			if (!fadeRightEl) return;

			const scrollDistanceRight =
				containerEl.scrollWidth - containerEl.clientWidth - containerEl.scrollLeft;
			fadeRightEl.style.opacity =
				fadeRight && fadeDistance
					? `${Math.min(0.95, scrollDistanceRight / fadeDistance)}`
					: "0";
		}, [fadeRight, fadeDistance]);

		// With useImperativeHandle we can give control to the parent, to scrollIntoView if the selected zone changes
		useImperativeHandle(
			parentRef,
			() => ({
				/** Used index instead of 'id' because item can also be an element */
				scrollIntoView: (
					index: number,
					{
						animate = true,
						margins = eXpress.math.margins(defaultScrollIntoViewMargin),
					}: {
						animate?: boolean;
						margins?: eXpress.math.IMargins;
					} = {},
				) => {
					if (!containerRef.current) return;
					const selectedEl = containerRef.current.children[index];
					if (!selectedEl) return;

					// Didn't use native scrollIntoView because it will not work when multiple menu's must scroll
					scrollIntoView(selectedEl, {
						margins,
						animate,
					});
				},
			}),
			[defaultScrollIntoViewMargin],
		);

		// Update when created or props changed
		useLayoutEffect(update, [update]);

		// Update UI when size changes
		useResizeObserver(containerRef, update, !fadeRight);

		// Try to detect changed children. (needed when component size doesn't change)
		// - Idea 1: Watch size of each child with ResizeObserver -> Doesn't work well as elements can be added/removed
		// - Idea 2: Adding one child and use resize observer on it -> no scrollIntoView possible because 1 child.
		// - Idea 3: Use MutationObserver to keep list up-to-date seems to work
		// - Idea 4: Expose the update function and let caller update this when needed -> responsibiliy to caller, seems to be more error prone
		// I did use Idea 3 here
		useEffect(() => {
			const containerEl = containerRef.current;
			if (!containerEl || !fadeRight) return undefined;

			const observer = new MutationObserver(update);

			observer.observe(containerEl, {
				childList: true,
				attributes: true,
				subtree: true,
			});

			return () => {
				observer.disconnect();
			};
		}, [update, fadeRight]);

		return (
			<div className={`horizontal-scrollable ${className ?? ""}`}>
				<div
					ref={containerRef}
					className={`horizontal-scrollable__container ${containerClassName ?? ""}`}
					onScroll={update}
				>
					{children}
				</div>
				<div className="horizontal-scrollable__fade-right" ref={fadeRightRef} />
			</div>
		);
	},
);
HorizontalScrollable.displayName = "HorizontalScrollable";
